import { AnimatePresence, motion } from "motion/react"
import { Badge } from "~/components/reusable/Badge"
import { useCompany } from "~/hooks/useCompany"
import { getMatchQualityForBadge, getMatchQualityPercentage } from "~/utils/matchQualityUtils"

type SimilarityScoreBadgeProps = Omit<React.ComponentProps<typeof Badge>, "variant"> & {
  similarity?: number | null
  isLoading?: boolean
  /** Custom (user-confirmed) matches render gray — the score isn't a system quality signal. */
  isCustomMatch?: boolean
  /** Percentage only, for rows too narrow for the quality label. The color still carries it. */
  compact?: boolean
}

export const SimilarityScoreBadge = ({
  similarity,
  isLoading = false,
  isCustomMatch = false,
  compact = false,
  size = "sm",
  ...badgeProps
}: SimilarityScoreBadgeProps) => {
  const { data: companyDefaults } = useCompany()

  if (isLoading) {
    return (
      <Badge size={size} variant="gray" {...badgeProps}>
        <AnimatePresence mode="wait">
          <motion.span
            className="font-medium text-lg animate-pulse"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            Loading Data
          </motion.span>
        </AnimatePresence>
      </Badge>
    )
  }

  if (similarity == null) {
    return (
      <Badge size={size} variant="red" {...badgeProps}>
        No data
      </Badge>
    )
  }

  const { text, variant } = getMatchQualityForBadge({
    similarity,
    brandMatchQuality: companyDefaults?.brandMatchQuality,
  })

  return (
    <Badge size={size} variant={isCustomMatch ? "gray" : variant} {...badgeProps}>
      {compact ? getMatchQualityPercentage(similarity) : text}
    </Badge>
  )
}
